// src/pages/finance-portal/payments/PaymentForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, Autocomplete, InputAdornment,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_METHODS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHECK', label: 'Check' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'DEBIT_CARD', label: 'Debit Card' },
    { value: 'ACH', label: 'ACH' },
    { value: 'ONLINE', label: 'Online' },
    { value: 'OTHER', label: 'Other' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function PaymentForm({ open, onClose, onSaved, preselectedCustomerId, preselectedInvoiceId }) {
    const { enqueueSnackbar } = useSnackbar();

    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerLoading, setCustomerLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [invoices, setInvoices] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [notes, setNotes] = useState('');

    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Fetch customers based on search
    const fetchCustomers = useCallback(async (search) => {
        setCustomerLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/customers', { params });
            if (res.data.success) {
                setCustomers(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch customers', err);
        } finally {
            setCustomerLoading(false);
        }
    }, []);

    // Fetch invoices for selected customer
    const fetchInvoices = useCallback(async (customerId) => {
        if (!customerId) {
            setInvoices([]);
            return;
        }
        setInvoiceLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/invoices', {
                params: { customer_id: customerId, status: 'SENT,PARTIALLY_PAID,OVERDUE,VIEWED' }
            });
            if (res.data.success) {
                setInvoices(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch invoices', err);
        } finally {
            setInvoiceLoading(false);
        }
    }, []);

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setError(null);
            setAmount('');
            setPaymentDate(new Date().toISOString().split('T')[0]);
            setPaymentMethod('BANK_TRANSFER');
            setReferenceNumber('');
            setNotes('');
            setSelectedInvoice(null);
            setSelectedCustomer(null);
            setInvoices([]);

            // Load initial customers
            fetchCustomers('');
        }
    }, [open, fetchCustomers]);

    // Handle preselected customer
    useEffect(() => {
        if (open && preselectedCustomerId && customers.length > 0) {
            const found = customers.find(c => c.id === preselectedCustomerId);
            if (found) {
                setSelectedCustomer(found);
                fetchInvoices(found.id);
            }
        }
    }, [open, preselectedCustomerId, customers, fetchInvoices]);

    // Handle preselected invoice
    useEffect(() => {
        if (open && preselectedInvoiceId && invoices.length > 0) {
            const found = invoices.find(inv => inv.id === preselectedInvoiceId);
            if (found) {
                setSelectedInvoice(found);
                setAmount(String(found.balance_due || found.total || ''));
            }
        }
    }, [open, preselectedInvoiceId, invoices]);

    // Fetch invoices when customer changes
    useEffect(() => {
        if (selectedCustomer) {
            fetchInvoices(selectedCustomer.id);
        } else {
            setInvoices([]);
            setSelectedInvoice(null);
        }
    }, [selectedCustomer, fetchInvoices]);

    // Debounced customer search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCustomers(customerSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [customerSearch, fetchCustomers]);

    const handleInvoiceSelect = (invoice) => {
        setSelectedInvoice(invoice);
        if (invoice) {
            setAmount(String(invoice.balance_due || invoice.total || ''));
        }
    };

    const handleSave = async () => {
        if (!selectedCustomer) {
            setError('Please select a customer');
            return;
        }
        if (!amount || parseFloat(amount) <= 0) {
            setError('Please enter a valid amount');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload = {
                customer_id: selectedCustomer.id,
                invoice_id: selectedInvoice?.id || null,
                amount: parseFloat(amount),
                payment_date: paymentDate,
                payment_method: paymentMethod,
                reference_number: referenceNumber || null,
                notes: notes || null,
            };

            await axiosInstance.post('/api/finance/payments', payload);
            enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
            onSaved?.();
            onClose();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to record payment';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                setError(msg);
            } else {
                setError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon sx={{ color: ACCENT }} />
                Record Payment
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '10px', fontFamily }}>{error}</Alert>}

                {/* Customer */}
                <Autocomplete
                    size="small"
                    options={customers}
                    getOptionLabel={(c) => c ? (c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '') : ''}
                    value={selectedCustomer}
                    onChange={(_, val) => setSelectedCustomer(val)}
                    onInputChange={(_, val) => setCustomerSearch(val)}
                    loading={customerLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Customer"
                            required
                            InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                            InputProps={{
                                ...params.InputProps,
                                sx: { fontFamily, borderRadius: '10px' },
                                endAdornment: (
                                    <>
                                        {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No customers found"
                />

                {/* Invoice */}
                <Autocomplete
                    size="small"
                    options={invoices}
                    getOptionLabel={(inv) => inv ? `${inv.invoice_number || 'INV-' + inv.id} - ${formatCurrency(inv.balance_due || inv.total)} (${inv.status})` : ''}
                    value={selectedInvoice}
                    onChange={(_, val) => handleInvoiceSelect(val)}
                    loading={invoiceLoading}
                    disabled={!selectedCustomer}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Invoice (optional)"
                            InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                            InputProps={{
                                ...params.InputProps,
                                sx: { fontFamily, borderRadius: '10px' },
                                endAdornment: (
                                    <>
                                        {invoiceLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText={selectedCustomer ? "No unpaid invoices" : "Select a customer first"}
                />

                {/* Balance Due Info */}
                {selectedInvoice && (
                    <Box sx={{
                        p: 2, borderRadius: '12px',
                        bgcolor: 'rgba(34,197,94,0.06)',
                        border: '1px solid rgba(34,197,94,0.2)',
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>Invoice Total</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>{formatCurrency(selectedInvoice.total)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>Amount Paid</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>{formatCurrency((selectedInvoice.total || 0) - (selectedInvoice.balance_due || 0))}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(34,197,94,0.2)', pt: 0.5 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: ACCENT }}>Balance Due</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(selectedInvoice.balance_due)}</Typography>
                        </Box>
                    </Box>
                )}

                {/* Amount */}
                <TextField
                    label="Amount"
                    size="small"
                    required
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    InputLabelProps={{ sx: { fontFamily } }}
                    InputProps={{
                        sx: { fontFamily, borderRadius: '10px' },
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                />

                {/* Date and Method row */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Payment Date"
                        type="date"
                        size="small"
                        required
                        value={paymentDate}
                        onChange={e => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontFamily }}>Payment Method</InputLabel>
                        <Select
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e.target.value)}
                            label="Payment Method"
                            sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {PAYMENT_METHODS.map(m => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontFamily }}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Reference Number */}
                <TextField
                    label="Reference Number"
                    size="small"
                    value={referenceNumber}
                    onChange={e => setReferenceNumber(e.target.value)}
                    InputLabelProps={{ sx: { fontFamily } }}
                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                    placeholder="Check #, transaction ID, etc."
                />

                {/* Notes */}
                <TextField
                    label="Notes"
                    size="small"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    InputLabelProps={{ sx: { fontFamily } }}
                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                    placeholder="Optional payment notes..."
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !selectedCustomer || !amount}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    {saving ? 'Recording...' : 'Record Payment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
